import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { isNativePlatform } from '@/services/platformService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Crown, ArrowLeft, ArrowRight, Check, ExternalLink, Sparkles, Link, Users, ShieldAlert, KeyRound, Upload, Phone } from 'lucide-react';
import LanguageSelector from '@/components/LanguageSelector';
import { TranslatedFileInput } from '@/components/ui/translated-file-input';
import { validateAmbassadorInviteCode } from '@/services/ambassadorInviteService';

const PLATFORMS = ['Instagram', 'TikTok', 'YouTube', 'X (Twitter)', 'Facebook', 'Twitch', 'Blog/Website'];

const AmbassadorOnboarding = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Form fields
  const [inviteCode, setInviteCode] = useState('');
  const [inviteCodeValid, setInviteCodeValid] = useState<boolean | null>(null);
  const [inviteCodeChecking, setInviteCodeChecking] = useState(false);
  const inviteValidationRequest = useRef(0);
  const [fullName, setFullName] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [platformDetails, setPlatformDetails] = useState<Record<string, { url: string; followers: string; isPrimary: boolean }>>({});
  const [paypalEmail, setPaypalEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [idDocumentFile, setIdDocumentFile] = useState<File | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    if (isNativePlatform()) {
      navigate('/', { replace: true });
      return;
    }
    if (!user) {
      navigate('/ambassador-login', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    const check = async () => {
      const { data } = await supabase
        .from('ambassador_applications')
        .select('status')
        .eq('user_id', user.id)
        .maybeSingle();
      if (data) {
        if (data.status === 'approved') navigate('/ambassador-dashboard', { replace: true });
        else navigate('/ambassador-login', { replace: true });
      }
    };
    check();
  }, [user, navigate]);

  if (isNativePlatform() || !user) return null;

  // Only ask for a boolean; invite rows are restricted to administrators.
  const validateInviteCode = async (code: string) => {
    const request = ++inviteValidationRequest.current;
    if (!code.trim()) {
      setInviteCodeValid(null);
      setInviteCodeChecking(false);
      return;
    }
    setInviteCodeChecking(true);
    try {
      const valid = await validateAmbassadorInviteCode(code);
      if (request === inviteValidationRequest.current) setInviteCodeValid(valid);
    } catch {
      if (request === inviteValidationRequest.current) {
        setInviteCodeValid(false);
        toast({ title: t('common.error'), description: t('common.tryAgain'), variant: 'destructive' });
      }
    } finally {
      if (request === inviteValidationRequest.current) setInviteCodeChecking(false);
    }
  };

  const togglePlatform = (platform: string) => {
    setSelectedPlatforms(prev => {
      if (prev.includes(platform)) {
        const newDetails = { ...platformDetails };
        delete newDetails[platform];
        setPlatformDetails(newDetails);
        return prev.filter(x => x !== platform);
      }
      setPlatformDetails(prev2 => ({ ...prev2, [platform]: { url: '', followers: '', isPrimary: prev.length === 0 } }));
      return [...prev, platform];
    });
  };

  const setPrimaryPlatform = (platform: string) => {
    setPlatformDetails(prev => {
      const updated: typeof prev = {};
      for (const key of Object.keys(prev)) {
        updated[key] = { ...prev[key], isPrimary: key === platform };
      }
      return updated;
    });
  };

  const updatePlatformDetail = (platform: string, field: 'url' | 'followers', value: string) => {
    setPlatformDetails(prev => ({
      ...prev,
      [platform]: { ...prev[platform], [field]: value }
    }));
  };

  const getTotalFollowers = () => {
    return selectedPlatforms.reduce((sum, p) => sum + (parseInt(platformDetails[p]?.followers) || 0), 0);
  };

  const MIN_FOLLOWERS = 1000;

  const getTier = (followers: number): 'nano' | 'micro' | 'macro' | 'mega' => {
    if (followers >= 500000) return 'mega';
    if (followers >= 50000) return 'macro';
    if (followers >= 10000) return 'micro';
    return 'nano';
  };

  const handleSubmit = async () => {
    if (!termsAccepted || !fullName || !idNumber || !paypalEmail || !contactPhone || !idDocumentFile || selectedPlatforms.length === 0 || !inviteCodeValid) return;
    setSubmitting(true);

    const totalFollowers = getTotalFollowers();
    const tier = getTier(totalFollowers);

    const platformsJson = selectedPlatforms.map(p => ({
      name: p,
      url: platformDetails[p]?.url || '',
      followers: parseInt(platformDetails[p]?.followers) || 0,
      isPrimary: platformDetails[p]?.isPrimary || false,
    }));

    // Upload ID document
    let idDocumentUrl = '';
    if (idDocumentFile) {
      const fileExt = idDocumentFile.name.split('.').pop();
      const fileName = `${user.id}/ambassador-id-${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from('provider-id-documents')
        .upload(fileName, idDocumentFile);
      if (uploadError) {
        toast({ title: t('common.error'), description: uploadError.message, variant: 'destructive' });
        setSubmitting(false);
        return;
      }
      idDocumentUrl = `provider-id-documents/${fileName}`;
    }

    // Insert ambassador application
    const { data: appData, error: appError } = await supabase
      .from('ambassador_applications')
      .insert({
        user_id: user.id,
        full_name: fullName,
        id_number: idNumber,
        contact_phone: contactPhone,
        id_document_url: idDocumentUrl,
        platforms: platformsJson as any,
        total_followers: totalFollowers,
        tier,
        paypal_email: paypalEmail,
        status: 'pending_verification' as any,
        terms_accepted_at: new Date().toISOString(),
        terms_version: 'v1.6',
        invite_code_used: inviteCode.trim().toUpperCase(),
      } as any)
      .select('id')
      .single();

    if (appError) {
      toast({ title: t('common.error'), description: appError.message, variant: 'destructive' });
      setSubmitting(false);
      return;
    }

    // Insert into ambassador_platforms table
    if (appData) {
      const platformInserts = selectedPlatforms.map(p => ({
        ambassador_id: appData.id,
        platform: p,
        profile_url: platformDetails[p]?.url || '',
        followers_count: parseInt(platformDetails[p]?.followers) || 0,
        is_primary: platformDetails[p]?.isPrimary || false,
      }));

      await supabase.from('ambassador_platforms').insert(platformInserts);

      // The server consumes the invitation atomically with the application insert.
    }

    setSubmitted(true);
    setSubmitting(false);
  };

  const canProceed = () => {
    if (step === 1) return inviteCodeValid === true && fullName.trim() !== '' && idNumber.trim() !== '' && contactPhone.trim() !== '' && !!idDocumentFile;
    if (step === 2) {
      if (selectedPlatforms.length === 0) return false;
      const allFilled = selectedPlatforms.every(p => {
        const d = platformDetails[p];
        return d && d.url.trim() !== '' && d.followers.trim() !== '' && parseInt(d.followers) > 0;
      });
      if (!allFilled) return false;
      return getTotalFollowers() >= MIN_FOLLOWERS;
    }
    if (step === 3) return paypalEmail.trim() !== '' && paypalEmail.includes('@');
    if (step === 4) return termsAccepted;
    return false;
  };

  const stepLabels = ['ambassador.onboarding.step1.short', 'ambassador.onboarding.step2.short', 'ambassador.onboarding.step3.short', 'ambassador.onboarding.step4.short'];

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#07070d] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-gradient-to-b from-green-900/10 to-transparent rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 flex items-center justify-center min-h-screen px-4">
          <div className="max-w-md text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500/20 to-green-500/10 border border-emerald-500/20 mb-6 shadow-[0_0_40px_rgba(16,185,129,0.15)]">
              <Check className="h-12 w-12 text-emerald-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">{t('ambassador.onboarding.success.title')}</h1>
            <p className="text-gray-400 mb-8 leading-relaxed">{t('ambassador.onboarding.success.description')}</p>
            <Button
              onClick={() => navigate('/ambassador-login')}
              className="bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black font-semibold rounded-xl px-8 h-12 shadow-[0_0_20px_rgba(245,158,11,0.15)]"
            >
              {t('ambassador.onboarding.success.back')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#07070d] relative overflow-hidden">
      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-20">
        <LanguageSelector variant="compact" className="bg-black/40 border-gray-700/50 text-gray-300" />
      </div>
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-gradient-to-b from-amber-900/8 to-transparent rounded-full blur-3xl" />
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: 'linear-gradient(rgba(255,215,0,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,215,0,0.3) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-12">
        <div className="w-full max-w-lg">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-600 mb-4 shadow-[0_0_30px_rgba(245,158,11,0.2)]">
              <Crown className="h-8 w-8 text-white drop-shadow-lg" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-1">{t('ambassador.onboarding.title')}</h1>
            <p className="text-gray-500 text-sm">{t('ambassador.onboarding.subtitle')}</p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-8 px-2">
            {[1, 2, 3, 4].map((s, i) => (
              <React.Fragment key={s}>
                <div className="flex flex-col items-center gap-1.5">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    s === step
                      ? 'bg-gradient-to-br from-amber-500 to-amber-600 text-black shadow-[0_0_15px_rgba(245,158,11,0.3)]'
                      : s < step
                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                      : 'bg-gray-800/50 text-gray-600 border border-gray-700/50'
                  }`}>
                    {s < step ? <Check className="h-4 w-4" /> : s}
                  </div>
                  <span className={`text-[10px] font-medium ${s === step ? 'text-amber-400' : s < step ? 'text-gray-500' : 'text-gray-700'}`}>
                    {t(stepLabels[i])}
                  </span>
                </div>
                {i < 3 && (
                  <div className={`flex-1 h-px mx-2 mt-[-16px] ${s < step ? 'bg-amber-500/30' : 'bg-gray-800'}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="bg-gradient-to-b from-[#12121a] to-[#0e0e16] border border-amber-500/10 rounded-2xl p-8 shadow-[0_4px_30px_rgba(0,0,0,0.4)]">
            {/* Step 1: Invite Code + Personal Info */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-amber-400" />
                  <h2 className="text-lg font-semibold text-white">{t('ambassador.onboarding.step1.title')}</h2>
                </div>

                {/* Mandatory Invite Code */}
                <div>
                  <Label className="text-gray-400 text-sm flex items-center gap-1.5">
                    <KeyRound className="h-3.5 w-3.5 text-amber-400" />
                    {t('ambassador.onboarding.step1.inviteCode', 'Invitation Code')} <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    value={inviteCode}
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase();
                      inviteValidationRequest.current += 1;
                      setInviteCodeChecking(false);
                      setInviteCode(val);
                      setInviteCodeValid(null);
                    }}
                    onBlur={() => validateInviteCode(inviteCode)}
                    className={`mt-1.5 bg-black/40 border-gray-700/50 text-white rounded-xl h-12 focus:border-amber-500/50 focus:ring-amber-500/20 font-mono tracking-wider uppercase ${
                      inviteCodeValid === true ? 'border-emerald-500/50' : inviteCodeValid === false ? 'border-red-500/50' : ''
                    }`}
                    placeholder={t('ambassador.onboarding.step1.inviteCodePlaceholder', 'Enter your invitation code')}
                  />
                  {inviteCodeChecking && (
                    <p className="text-gray-500 text-xs mt-1.5">{t('ambassador.onboarding.step1.validating', 'Validating...')}</p>
                  )}
                  {inviteCodeValid === true && (
                    <p className="text-emerald-400 text-xs mt-1.5 flex items-center gap-1">
                      <Check className="h-3 w-3" /> {t('ambassador.onboarding.step1.inviteCodeValid', 'Valid invitation code')}
                    </p>
                  )}
                  {inviteCodeValid === false && (
                    <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                      <ShieldAlert className="h-3 w-3" /> {t('ambassador.onboarding.step1.inviteCodeInvalid', 'Invalid or already used invitation code')}
                    </p>
                  )}
                  <p className="text-gray-600 text-xs mt-1.5">{t('ambassador.onboarding.step1.inviteCodeNote', 'This code is provided by the Petius administrator.')}</p>
                </div>

                <div>
                  <Label className="text-gray-400 text-sm">{t('ambassador.onboarding.step1.fullName')}</Label>
                  <Input value={fullName} onChange={(e) => setFullName(e.target.value)} className="mt-1.5 bg-black/40 border-gray-700/50 text-white rounded-xl h-12 focus:border-amber-500/50 focus:ring-amber-500/20" placeholder={t('ambassador.onboarding.step1.fullNamePlaceholder')} />
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">{t('ambassador.onboarding.step1.idNumber')}</Label>
                  <Input value={idNumber} onChange={(e) => setIdNumber(e.target.value)} className="mt-1.5 bg-black/40 border-gray-700/50 text-white rounded-xl h-12 focus:border-amber-500/50 focus:ring-amber-500/20" placeholder={t('ambassador.onboarding.step1.idPlaceholder')} />
                </div>

                {/* Contact Phone */}
                <div>
                  <Label className="text-gray-400 text-sm flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5 text-amber-400" />
                    {t('ambassador.onboarding.step1.contactPhone', 'Contact Phone Number')} <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    className="mt-1.5 bg-black/40 border-gray-700/50 text-white rounded-xl h-12 focus:border-amber-500/50 focus:ring-amber-500/20"
                    placeholder={t('ambassador.onboarding.step1.contactPhonePlaceholder', '+1 (555) 123-4567')}
                    type="tel"
                  />
                </div>

                {/* ID Document Upload */}
                <div>
                  <Label className="text-gray-400 text-sm flex items-center gap-1.5">
                    <Upload className="h-3.5 w-3.5 text-amber-400" />
                    {t('ambassador.onboarding.step1.idDocument', 'ID Document (Photo)')} <span className="text-red-400">*</span>
                  </Label>
                  <p className="text-gray-600 text-xs mt-1">{t('ambassador.onboarding.step1.idDocumentNote', 'Upload a clear photo of your government-issued ID for identity verification.')}</p>
                  <TranslatedFileInput
                    accept="image/*"
                    value={idDocumentFile}
                    onChange={(file) => setIdDocumentFile(file)}
                    className="mt-1.5"
                  />
                </div>
              </div>
            )}

            {/* Step 2: Platforms with per-platform details */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-amber-400" />
                  <h2 className="text-lg font-semibold text-white">{t('ambassador.onboarding.step2.title')}</h2>
                </div>
                <div>
                  <Label className="text-gray-400 text-sm mb-3 block">{t('ambassador.onboarding.step2.platforms')}</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {PLATFORMS.map((p) => (
                      <button
                        key={p}
                        onClick={() => togglePlatform(p)}
                        className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all border ${
                          selectedPlatforms.includes(p)
                            ? 'bg-amber-500/10 border-amber-500/40 text-amber-300 shadow-[0_0_10px_rgba(245,158,11,0.08)]'
                            : 'bg-black/30 border-gray-700/50 text-gray-500 hover:border-gray-600 hover:text-gray-400'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                {selectedPlatforms.length > 0 && (
                  <div className="space-y-4">
                    <Label className="text-gray-400 text-sm">{t('ambassador.onboarding.step2.platformDetails', 'Platform details')}</Label>
                    {selectedPlatforms.map((platform) => (
                      <div key={platform} className="bg-black/20 border border-gray-800/50 rounded-xl p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-amber-300">{platform}</p>
                          <button
                            onClick={() => setPrimaryPlatform(platform)}
                            className={`text-[10px] px-2 py-1 rounded-full border transition-all ${
                              platformDetails[platform]?.isPrimary
                                ? 'bg-amber-500/20 border-amber-500/40 text-amber-300'
                                : 'border-gray-700/50 text-gray-600 hover:text-gray-400'
                            }`}
                          >
                            {platformDetails[platform]?.isPrimary ? `★ ${t('ambassador.onboarding.step2.primary', 'Primary')}` : t('ambassador.onboarding.step2.setPrimary', 'Set Primary')}
                          </button>
                        </div>
                        <div className="flex gap-3">
                          <div className="flex-1">
                            <div className="relative">
                              <Link className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-600" />
                              <Input
                                value={platformDetails[platform]?.url || ''}
                                onChange={(e) => updatePlatformDetail(platform, 'url', e.target.value)}
                                className="pl-9 bg-black/40 border-gray-700/50 text-white rounded-lg h-10 text-sm focus:border-amber-500/50 focus:ring-amber-500/20"
                                placeholder={t('ambassador.onboarding.step2.profileUrlPlaceholder', 'https://...')}
                              />
                            </div>
                          </div>
                          <div className="w-32">
                            <div className="relative">
                              <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-600" />
                              <Input
                                type="number"
                                value={platformDetails[platform]?.followers || ''}
                                onChange={(e) => updatePlatformDetail(platform, 'followers', e.target.value)}
                                className="pl-9 bg-black/40 border-gray-700/50 text-white rounded-lg h-10 text-sm focus:border-amber-500/50 focus:ring-amber-500/20"
                                placeholder={t('ambassador.onboarding.step2.followersPlaceholder', 'Followers')}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-between items-center bg-amber-500/5 border border-amber-500/15 rounded-xl px-4 py-3">
                      <span className="text-sm text-gray-400">{t('ambassador.onboarding.step2.totalFollowers', 'Total followers (all platforms)')}</span>
                      <span className="text-lg font-bold text-amber-400">{getTotalFollowers().toLocaleString()}</span>
                    </div>
                    {getTotalFollowers() > 0 && getTotalFollowers() < MIN_FOLLOWERS && (
                      <div className="bg-red-950/30 border border-red-500/30 rounded-xl px-4 py-3 flex items-start gap-2">
                        <ShieldAlert className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
                        <p className="text-red-400 text-xs leading-relaxed">
                          {t('ambassador.onboarding.step2.minimumFollowers', 'A minimum of 1,000 total followers across all platforms is required to qualify for the Ambassador Program.')}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: PayPal */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-amber-400" />
                  <h2 className="text-lg font-semibold text-white">{t('ambassador.onboarding.step3.title')}</h2>
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">{t('ambassador.onboarding.step3.paypalEmail')}</Label>
                  <Input type="email" value={paypalEmail} onChange={(e) => setPaypalEmail(e.target.value)} className="mt-1.5 bg-black/40 border-gray-700/50 text-white rounded-xl h-12 focus:border-amber-500/50 focus:ring-amber-500/20" placeholder="your@paypal.com" />
                  <p className="text-gray-600 text-xs mt-2">{t('ambassador.onboarding.step3.paypalNote')}</p>
                </div>
                <a href="https://www.paypal.com/signup" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-amber-400/70 hover:text-amber-300 text-sm transition-colors">
                  {t('ambassador.onboarding.step3.createPaypal')} <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            )}

            {/* Step 4: Legal */}
            {step === 4 && (
              <div className="space-y-6">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4 text-amber-400" />
                  <h2 className="text-lg font-semibold text-white">{t('ambassador.onboarding.step4.title')}</h2>
                </div>
                <p className="text-gray-500 text-sm">{t('ambassador.onboarding.step4.description')}</p>
                <div className="flex items-start gap-3 bg-black/20 rounded-xl p-4 border border-gray-800/50">
                  <Checkbox
                    id="terms"
                    checked={termsAccepted}
                    onCheckedChange={(checked) => setTermsAccepted(checked === true)}
                    className="mt-0.5 border-gray-600 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                  />
                  <label htmlFor="terms" className="text-gray-400 text-sm leading-relaxed">
                    {t('ambassador.onboarding.step4.accept')}{' '}
                    <a href="/ambassador-terms" target="_blank" className="text-amber-400 hover:text-amber-300 underline underline-offset-2">
                      {t('ambassador.onboarding.step4.termsLink')}
                    </a>
                  </label>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-800/50">
              <Button
                variant="ghost"
                onClick={() => step > 1 ? setStep(step - 1) : navigate('/ambassador-login')}
                className="text-gray-500 hover:text-white hover:bg-white/5"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('common.back')}
              </Button>

              {step < 4 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={!canProceed()}
                  className="bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black font-semibold rounded-xl px-6 shadow-[0_0_15px_rgba(245,158,11,0.12)] disabled:opacity-30 disabled:shadow-none"
                >
                  {t('ambassador.onboarding.next')}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!canProceed() || submitting}
                  className="bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black font-semibold rounded-xl px-6 shadow-[0_0_20px_rgba(245,158,11,0.15)] disabled:opacity-30 disabled:shadow-none"
                >
                  {submitting ? t('common.processing') : t('ambassador.onboarding.submit')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmbassadorOnboarding;
